// =============================================================================
// PORTFOLIO CENTRAL DATA MODULE (Single Source of Truth)
// Edit this file to update profile, bio, skills, experience, projects, or API specs.
// =============================================================================

export interface Profile {
  name: string;
  shortName: string;
  role: string;
  location: string;
  timezone: string;
  timezoneDisplay: string;
  email: string;
  phone: string;
  phoneDisplay: string;
  website: string;
  github: string;
  githubHandle: string;
  linkedin: string;
  linkedinHandle: string;
  availability: string;
}

export interface Project {
  name: string;
  url: string;
  description: string;
  tags: string[];
}

export interface ExperienceItem {
  role: string;
  period: string;
  points: string[];
}

export interface SpokenLanguage {
  name: string;
  level: string;
}

export interface TechStackItem {
  name: string;
  label: string;
}

export interface StatementHighlight {
  text: string;
  underline: boolean;
}

export interface ArcadeFeature {
  title: string;
  description: string;
}

// -----------------------------------------------------------------------------
// 1. Profile & Contact Information
// -----------------------------------------------------------------------------
export const profile: Profile = {
  name: "Mohammad Hosein Roustaie",
  shortName: "Roustaie",
  role: "Backend Developer",
  location: "Tehran, Iran",
  timezone: "Asia/Tehran",
  timezoneDisplay: "IRST (UTC+03:30)",
  email: "mail@creasy.ir",
  phone: "+989357342145",
  phoneDisplay: "+98 935 734 2145",
  website: "https://creasy.ir",
  github: "https://github.com/creasydude",
  githubHandle: "creasydude",
  linkedin: "https://www.linkedin.com/in/roustaie-mh/",
  linkedinHandle: "roustaie-mh",
  availability: "Open to consulting & contracts",
};

// -----------------------------------------------------------------------------
// 2. Core Skills (Used across the site, terminal, and returned by /v1/engineer)
// -----------------------------------------------------------------------------
export const skillsList: string[] = [
  "TypeScript",
  "JavaScript",
  "Node.js",
  "Next.js",
  "MongoDB",
  "PostgreSQL",
  "SQLite",
  "ORM",
  "Linux",
  "Git",
  "Docker",
  "Redis",
  "AI Agents",
  "Clean Architecture",
];

export const skills: Record<string, string[]> = {
  "Languages & Runtimes": ["TypeScript", "JavaScript", "Python", "Node.js", "Next.js"],
  "Databases & Storage": ["PostgreSQL", "Redis", "MongoDB", "SQLite"],
  "Infrastructure & Edge": ["Docker", "Linux", "Cloudflare", "Git"],
  "AI Systems & Tooling": ["AI Agents", "Claude Code", "Codex", "MCP Tooling", "Prompt Engineering"],
};

// -----------------------------------------------------------------------------
// 3. Hero Section Configuration
// -----------------------------------------------------------------------------
export const heroGreetings: string[] = [
  "Hello",
  "Salam",
  "Hola",
  "Bonjour",
  "Ciao",
  "Dorood",
  "Servus",
];

export const heroTechStack: TechStackItem[] = [
  { name: "JavaScript", label: "Languages" },
  { name: "TypeScript", label: "Languages" },
  { name: "Node.js", label: "Runtime" },
  { name: "Next.js", label: "Runtime" },
  { name: "PostgreSQL", label: "Database" },
  { name: "MongoDB", label: "Database" },
  { name: "SQLite", label: "Database" },
  { name: "Python", label: "For Automation" },
  { name: "Linux", label: "Operating System" },
  { name: "Docker", label: "Container" },
  { name: "Git", label: "Version Control" },
  { name: "Redis", label: "Caching" },
  { name: "Cloudflare", label: "Edge" },
  { name: "Claude Code", label: "Harness" },
  { name: "Codex", label: "Harness" },
];

export const heroContent = {
  headline: "Mohammad Hosein Roustaie",
  statementLead: "I'm a backend developer building",
  statementHighlights: [
    { text: "scalable APIs", underline: true },
    { text: "robust microservices", underline: true },
    { text: "autonomous AI agent workflows", underline: true },
  ],
  bio: "Based in Tehran, focusing on clean service boundaries, high-availability architecture, and using large language models to build real production systems rather than toy demos.",
};

// -----------------------------------------------------------------------------
// 4. About & Technical Philosophy
// -----------------------------------------------------------------------------
export const aboutContent = {
  sectionTag: "Background & Principles",
  title: "About & Technical Philosophy",
  subtitle: "Backend Architecture · 2019 — Present",
  bioParagraphs: [
    "I primarily work with TypeScript, JavaScript, and Node.js for development. I also use Python for automation, scripting, and other tasks where it makes sense.",
    "I'm particularly interested in API development and software architecture. I enjoy building things that are well-structured, practical, and easy to maintain.",
    "I'm also really interested in large language models (LLMs) and AI agents, and I use them a lot in my day-to-day work. Yeah, that's pretty much it!"
  ],
};

// Backward-compatible string export
export const about = aboutContent.bioParagraphs.join("\n\n");

// -----------------------------------------------------------------------------
// 5. Experience History
// -----------------------------------------------------------------------------
export const experience: ExperienceItem[] = [
  {
    role: "Freelance Backend Developer",
    period: "2019 — Present",
    points: [
      "Designed and built backend APIs and services for clients across different projects, mainly in Node.js and TypeScript.",
      "Handled deployment end-to-end on Linux servers using Docker, rather than just the application code.",
      "Increasingly folding AI agents and LLM tooling into the actual development workflow, not just as a side experiment.",
    ],
  },
];

// -----------------------------------------------------------------------------
// 6. Spoken Languages
// -----------------------------------------------------------------------------
export const languages: SpokenLanguage[] = [
  { name: "Persian", level: "Native" },
  { name: "English", level: "Intermediate" },
  { name: "German", level: "Basic" },
];

// -----------------------------------------------------------------------------
// 7. Selected Work & Projects
// -----------------------------------------------------------------------------
export const projectsContent = {
  sectionTag: "Works",
  title: "Projects & Implementations",
  subtitle: "Functional backend systems, service prototypes, and tools in active use.",
};

export const projects: Project[] = [
  {
    name: "Personal Toolkit",
    url: "https://plusking.ir",
    description:
      "A small collection of tools I actually use — day-to-day utilities I got tired of not having. Source for most of them is on GitHub.",
    tags: ["TypeScript", "Node.js"],
  },
  {
    name: "Exam App",
    url: "https://github.com/creasydude/ExamApp",
    description:
      "Backend for an online exam platform, built for COVID-era remote student testing.",
    tags: ["Node.js", "Express"],
  },
  {
    name: "Ecommerce Microservices",
    url: "https://github.com/creasydude/SimpleMicroserviceEcommerce",
    description:
      "A small e-commerce backend split into services — built to practice clean architecture and service boundaries with Express.js.",
    tags: ["Express.js", "Microservices", "Clean Architecture"],
  },
  {
    name: "More on GitHub",
    url: "https://github.com/creasydude",
    description:
      "Most of what I build ends up published here — worth a look if the above was interesting.",
    tags: [],
  },
];

// -----------------------------------------------------------------------------
// 8. Interactive Lab & Ghostty Arcade Configuration
// -----------------------------------------------------------------------------
export const arcadeContent = {
  sectionTag: "Interactive Lab",
  title: "Terminal & ASCII Arcade",
  subtitle:
    "Play the ASCII Flappy Bird minigame, inspect developer specs via neofetch, or query the live edge API through the interactive shell.",
  features: [
    {
      title: "60FPS Monospace Loop",
      description: "Smooth requestAnimationFrame physics",
    },
    {
      title: "Web Audio 8-Bit Synthesis",
      description: "Native dual-oscillator sound effects",
    },
    {
      title: "Interactive Shell",
      description: "Real API calls & terminal history",
    },
  ],
};

// -----------------------------------------------------------------------------
// 9. Contact Section Configuration
// -----------------------------------------------------------------------------
export const contactContent = {
  sectionTag: "Let's Connect",
  title: "Start a Conversation",
  directEmailNote:
    "Direct inbox is the fastest route for contracts, design audits, and technical advisory. Typical response within 24 hours.",
};

// -----------------------------------------------------------------------------
// 10. Live Engineer API Response Payload (Served at /v1/engineer)
// -----------------------------------------------------------------------------
export const engineerApiResponse = {
  engineer: profile.name,
  role: profile.role,
  location: `${profile.location} (IRST / UTC+3:30)`,
  availability: profile.availability,
  skills: skillsList,
  direct_inbox: profile.email,
};
